import { useState, useMemo } from 'react';
import {
  Container,
  Box,
  Flex,
  Heading,
  Text,
  Button,
  SimpleGrid,
  Tabs,
  TabList,
  Tab,
  useColorModeValue,
  useDisclosure,
  useToast,
  Spinner,
  Center,
  VStack,
  Badge,
  HStack,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { useCRMIdeas } from './useCRMIdeas';
import { CRMIdeaCard } from './components/CRMIdeaCard';
import { CRMAddIdeaModal } from './components/CRMAddIdeaModal';

/**
 * CRM Ideas Page - View and manage all submitted feedback/ideas
 * Admin-only page for CRM Admins and SuperAdmins
 */
export function CRMIdeasPage() {
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const toast = useToast();
  const {
    isOpen: isAddOpen,
    onOpen: onAddOpen,
    onClose: onAddClose,
  } = useDisclosure();

  const { ideas, loading, updateIdea, deleteIdea } = useCRMIdeas(currentUser?.uid);

  const [typeFilter, setTypeFilter] = useState<'all' | 'idea' | 'fix'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'new' | 'completed'>('all');

  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');

  const headingColor = useColorModeValue('gray.900', 'white');
  const subheadingColor = useColorModeValue('gray.600', 'gray.400');
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Filter ideas
  const filteredIdeas = useMemo(() => {
    let filtered = ideas;

    if (typeFilter !== 'all') {
      filtered = filtered.filter((idea) => idea.type === typeFilter);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((idea) => idea.status === statusFilter);
    }

    return filtered;
  }, [ideas, typeFilter, statusFilter]);

  // Stats
  const stats = useMemo(() => {
    const totalIdeas = ideas.filter(i => i.type === 'idea').length;
    const totalFixes = ideas.filter(i => i.type === 'fix').length;
    const newItems = ideas.filter(i => i.status === 'new').length;
    const completedItems = ideas.filter(i => i.status === 'completed').length;
    return { totalIdeas, totalFixes, newItems, completedItems };
  }, [ideas]);

  const handleComplete = async (ideaId: string) => {
    try {
      await updateIdea(
        ideaId, 
        { status: 'completed' },
        currentUser?.displayName || currentUser?.email || 'Admin'
      );
      toast({
        title: 'Idea marked as completed',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: 'Error completing idea',
        description: (error as Error).message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleDelete = async (ideaId: string) => {
    if (!window.confirm('Are you sure you want to delete this idea? This action cannot be undone.')) {
      return;
    }
    
    try {
      await deleteIdea(ideaId);
      toast({
        title: 'Idea deleted',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: 'Error deleting idea',
        description: (error as Error).message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Center h="60vh" flexDirection="column">
          <Spinner size="xl" color="blue.500" thickness="4px" mb={4} />
          <Text color="gray.600">Loading ideas...</Text>
        </Center>
      </Container>
    );
  }

  return (
    <Box bg={bgColor} minH="100vh">
      <Container maxW="container.xl" py={8}>
        {/* Header */}
        <Flex justify="space-between" align="center" mb={6}>
          <Box>
            <Heading size="lg" color={headingColor} mb={2}>
              💡 CRM Ideas & Feedback
            </Heading>
            <Text color={subheadingColor}>
              View and manage feedback submitted by CRM users
            </Text>
          </Box>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            onClick={onAddOpen}
          >
            Submit Idea
          </Button>
        </Flex>

        {/* Stats Cards */}
        <Box bg={cardBg} p={5} borderRadius="lg" shadow="sm" mb={6} borderWidth="1px" borderColor={borderColor}>
          <StatGroup>
            <Stat>
              <StatLabel>Total Ideas</StatLabel>
              <StatNumber color="blue.500">{stats.totalIdeas}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Bug Reports</StatLabel>
              <StatNumber color="orange.500">{stats.totalFixes}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Pending</StatLabel>
              <StatNumber color="yellow.500">{stats.newItems}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Completed</StatLabel>
              <StatNumber color="green.500">{stats.completedItems}</StatNumber>
            </Stat>
          </StatGroup>
        </Box>

        {/* Type Filter */}
        <Tabs
          index={typeFilter === 'all' ? 0 : typeFilter === 'idea' ? 1 : 2}
          onChange={(index) => {
            const types: ('all' | 'idea' | 'fix')[] = ['all', 'idea', 'fix'];
            setTypeFilter(types[index]);
          }}
          colorScheme="blue"
          mb={4}
        >
          <TabList>
            <Tab>All Types</Tab>
            <Tab>
              <HStack spacing={2}>
                <Text>💡 Ideas</Text>
                <Badge colorScheme="blue">{stats.totalIdeas}</Badge>
              </HStack>
            </Tab>
            <Tab>
              <HStack spacing={2}>
                <Text>🔧 Fixes</Text>
                <Badge colorScheme="orange">{stats.totalFixes}</Badge>
              </HStack>
            </Tab>
          </TabList>
        </Tabs>

        {/* Status Filter */}
        <Tabs
          index={statusFilter === 'all' ? 0 : statusFilter === 'new' ? 1 : 2}
          onChange={(index) => {
            const statuses: ('all' | 'new' | 'completed')[] = ['all', 'new', 'completed'];
            setStatusFilter(statuses[index]);
          }}
          colorScheme="blue"
          mb={6}
        >
          <TabList>
            <Tab>All Status</Tab>
            <Tab>
              <HStack spacing={2}>
                <Text>🆕 New</Text>
                <Badge colorScheme="yellow">{stats.newItems}</Badge>
              </HStack>
            </Tab>
            <Tab>
              <HStack spacing={2}>
                <Text>✓ Completed</Text>
                <Badge colorScheme="green">{stats.completedItems}</Badge>
              </HStack>
            </Tab>
          </TabList>
        </Tabs>

        {/* Ideas Grid */}
        {filteredIdeas.length === 0 ? (
          <Box textAlign="center" py={12} bg={cardBg} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
            <VStack spacing={4}>
              <Text fontSize="4xl">📭</Text>
              <Text color={subheadingColor} fontSize="lg">
                No ideas found
              </Text>
              <Text color={subheadingColor} fontSize="sm">
                {typeFilter !== 'all' || statusFilter !== 'all' 
                  ? 'Try adjusting your filters' 
                  : 'Ideas submitted by CRM users will appear here'}
              </Text>
              <Button colorScheme="blue" onClick={onAddOpen}>
                Submit First Idea
              </Button>
            </VStack>
          </Box>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {filteredIdeas.map((idea) => (
              <CRMIdeaCard
                key={idea.id}
                idea={idea}
                onComplete={() => handleComplete(idea.id)}
                onDelete={() => handleDelete(idea.id)}
                canComplete={isCRMAdmin || false}
                canDelete={SuperAdmin === true}
              />
            ))}
          </SimpleGrid>
        )}

        {/* Add Idea Modal */}
        <CRMAddIdeaModal
          isOpen={isAddOpen}
          onClose={onAddClose}
        />
      </Container>
    </Box>
  );
}

export default CRMIdeasPage;



